import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Minus, ShoppingCart, RotateCcw, AlertTriangle, Filter,
  ChevronLeft, ChevronRight, Clock, User, FileText
} from 'lucide-react';

const movementTypes = [
  { id: 'all', label: 'All' },
  { id: 'ADD', label: 'Add', icon: Plus, color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30' },
  { id: 'REMOVE', label: 'Remove', icon: Minus, color: 'text-red-600 bg-red-100 dark:bg-red-900/30' },
  { id: 'SALE', label: 'Sale', icon: ShoppingCart, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30' },
  { id: 'RETURN', label: 'Return', icon: RotateCcw, color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30' },
  { id: 'DAMAGE', label: 'Damage', icon: AlertTriangle, color: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30' },
];

const pageSizes = [10, 20, 50];

function getMovementIcon(type) {
  const found = movementTypes.find((m) => m.id === type);
  return found?.icon || AlertTriangle;
}

function getMovementColor(type) {
  const found = movementTypes.find((m) => m.id === type);
  return found?.color || 'text-gray-600 bg-gray-100 dark:bg-gray-700';
}

function getMovementLabel(type) {
  const found = movementTypes.find((m) => m.id === type);
  return found?.label || type;
}

export default function StockMovementList({ movements = [], loading = false }) {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const filtered = filter === 'all' ? movements : movements.filter((m) => m.type === filter);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterChange = (type) => {
    setFilter(type);
    setPage(1);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={16} className="text-gray-400" />
          {movementTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleFilterChange(type.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                filter === type.id
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
              <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <FileText size={36} className="mb-2" />
            <p className="text-sm font-medium">No stock movements found</p>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {paginated.map((movement, idx) => {
              const Icon = getMovementIcon(movement.type);
              const colorClass = getMovementColor(movement.type);
              const isPositive = movement.type === 'ADD' || movement.type === 'RETURN';
              return (
                <motion.div
                  key={movement.id || movement._id || idx}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  layout
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <Icon size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {getMovementLabel(movement.type)}
                      </span>
                      <span className={`text-sm font-bold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                        {isPositive ? '+' : ''}{movement.quantityChange || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {movement.previousStock !== undefined && movement.newStock !== undefined && (
                        <span className="flex items-center gap-1">
                          <FileText size={10} />
                          {movement.previousStock} {'\u2192'} {movement.newStock}
                        </span>
                      )}
                      {movement.reason && (
                        <span className="flex items-center gap-1">
                          <FileText size={10} />
                          {movement.reason}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 space-y-1">
                    <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 justify-end">
                      <Clock size={10} />
                      {movement.timestamp
                        ? new Date(movement.timestamp).toLocaleDateString('en-PK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : ''}
                    </div>
                    {movement.recordedBy && (
                      <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 justify-end">
                        <User size={10} />
                        {movement.recordedBy}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {filtered.length > pageSize && (
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
              className="text-xs bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-300 focus:outline-none"
            >
              {pageSizes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
